import { Form, Input, InputNumber, Popconfirm, Table, Tag } from "antd";
import type { FormInstance } from "antd/es/form";
import React, { useContext, useEffect, useRef, useState } from "react";
import "./productFormCtrl.css";
import { ColumnTypes, IProductItem } from "../product.interface";
import Image from "@src/packages/pro-component/schema/Image";
import _, { uniqueId } from "lodash";
import { v4 as uuid } from "uuid";

const EditableContext = React.createContext<FormInstance<any> | null>(null);

interface Item {
  key: string;
  name: string;
  images: string[];
  originPrice: string;
  paymentCash: string;
}

interface EditableRowProps {
  index: number;
}

const colors = [
  "pink",
  "red",
  "yellow",
  "orange",
  "cyan",
  "green",
  "blue",
  "purple",
  "geekblue",
  "magenta",
  "volcano",
  "gold",
  "lime",
];

const inputNumberKey = ["inputPrice", "originPrice", "paymentCash", "quantity"];

let colorSet: any = {};

const getColor = (property: string, value: string) => {
  const key = `${property}-${value}`;
  if (colorSet[key]) return colorSet[key];
  colorSet[key] = colors[Math.floor(Math.random() * colors.length)];
  return colorSet[key];
};

export const EditableRow: React.FC<EditableRowProps> = ({
  index,
  ...props
}) => {
  return <tr {...props} />;
};

interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  children: React.ReactNode;
  dataIndex: keyof Item;
  record: Item;
  handleSave: (record: Item) => void;
}

export const EditableCell: React.FC<EditableCellProps> = ({
  title,
  children,
  dataIndex,
  record,
  editable,
  handleSave,
  ...restProps
}) => {
  let childNode = children;
  if (editable) {
    if (dataIndex === "images") {
      childNode = (
        <Form.Item name={[`${record.key}`, dataIndex]}>
          <Image multiple />
        </Form.Item>
      );
    } else {
      childNode = (
        <Form.Item style={{ margin: 0 }} name={[`${record.key}`, dataIndex]}>
          {inputNumberKey.includes(dataIndex) ? <InputNumber disabled /> : <Input />}
        </Form.Item>
      );
    }
  }
  return <td {...restProps}>{childNode}</td>;
};

interface IAttribution {
  property: string;
  values: string[];
}

const TableProduct = ({ form }: any) => {
  const [dataSource, setDataSource] = useState<IProductItem[]>([]);

  useEffect(() => {
    let attribution = form.getFieldValue("attribution");
    if (!attribution) return;
    attribution = attribution.filter(
      (i: any) => !!i && !!i.property && !!i.values
    );
    if (!attribution.length) return setDataSource([]);
    let products = generateProducts(attribution);
    setDataSource(products);
  }, [form.getFieldValue("attribution")]);

  function generateProducts(attributions: IAttribution[]) {
    let products: any[] = [{}];
    const defaultValue: any = {
      name: "",
      attValues: [],
      images: [],
      originPrice: "",
      paymentCash: "",
    };

    for (const { property, values = [] } of attributions) {
      const newProducts = [];
      for (const product of products) {
        for (const val of values) {
          let create = {
            ...defaultValue,
            ...product,
            attValues: (product["attValues"] || []).concat({
              property,
              value: val,
              color: getColor(property, val),
            }),
          };
          newProducts.push(create);
        }
      }
      products = newProducts;
    }
    products.map((item) => {
      item.key = item.attValues
        .map((att: any) => att.value)
        .sort()
        .join("-");
    });
    return products;
  }

  const handleDelete = (key: any) => {
    const newData = dataSource.filter((item) => item.key !== key);
    setDataSource(newData);
  };

  const defaultColumns: (ColumnTypes[number] & {
    editable?: boolean;
    form?: boolean;
    number?: boolean;
    dataIndex: string;
  })[] = [
    {
      title: "Tên sản phẩm",
      dataIndex: "name",
      editable: true,
    },
    {
      title: "Thuộc tính",
      dataIndex: "attValues",
      render: (attValues: any[]) => {
        return attValues.map((att, index) => (
          <Tag key={att + index} color={att.color}>
            {att.value}
          </Tag>
        ));
      },
    },
    {
      title: "Ảnh",
      dataIndex: "images",
      editable: true,
    },
    {
      title: "Giá gốc",
      dataIndex: "originPrice",
      editable: true,
    },
    {
      title: "Giá bán",
      dataIndex: "paymentCash",
      editable: true,
    },
    // {
    //   title: "Số lượng",
    //   dataIndex: "quantity",
    //   // editable: true,
    // },
    // {
    //   title: "Thao tác",
    //   dataIndex: "operation",
    //   render: (_, record: any) =>
    //     dataSource.length >= 1 ? (
    //       <Popconfirm
    //         title="Bạn có chắc chắn?"
    //         onConfirm={() => handleDelete(record.key)}
    //       >
    //         <a style={{ color: "red" }}>Xoá</a>
    //       </Popconfirm>
    //     ) : null,
    // },
  ];
  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: IProductItem) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
      }),
    };
  });
  return (
    <>
      <p>Danh sách sản phẩm</p>
      <Table
        components={{
          body: {
            row: EditableRow,
            cell: EditableCell,
          },
        }}
        rowClassName={() => "editable-row"}
        bordered
        pagination={false}
        dataSource={dataSource}
        columns={columns as ColumnTypes}
      />
    </>
  );
};

export default TableProduct;
